import React, { Fragment, useEffect, useState } from "react";
import {
  assoc,
  differenceWith,
  filter,
  flatten,
  includes,
  isEmpty,
  map,
  pipe,
  pluck,
  prop,
  propEq,
  sortBy,
  splitEvery,
  pick,
  toLower,
  reject,
  equals,
} from "ramda";
import { useDispatch, useSelector } from "react-redux";
import {
  COMPLETE_STEP,
  setCoverFile,
  changeWorkFiles,
  updateWorkFileUploadProgress,
  updateWorkFiles,
  WORK_TYPE_FIELD_BLURRED,
  updateCoverFileUploadProgress,
} from "upload/uploadActions";
import {
  changeSubmissionFiles,
  SHOW_CANCEL_SUBMISSION_DIALOG,
  SET_WORK_TYPE_ID,
  SET_ACTIVE_STEP,
  UPDATE_SUBMISSION_FILES,
} from "submission/submissionActions";
import {
  getCoverFile,
  getProjectHideCoverImageUpload,
  getStepErrors,
  getStepLoadingLocalStorage,
  getSubmissionFiles,
  getWorkFiles,
  getWorkType,
  getWorkTypes,
} from "common/selectors";
import { mapUploadFileData } from "../uploadUtils";
import { useRequestUploadUrl, useUploadFile } from "../uploadServices";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import FileUploadInput from "general-components/FileUploadInput";
import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlTextField from "../../general-components/IntlTextField";
import LoadingPanel from "general-components/LoadingPanel";
import StepPanelButtonBar from "general-components/StepPanelButtonBar";

import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";

import * as steps from "common/steps";
import { useTranslation } from "react-i18next";
import { RequestError } from "../../common/fetchUtils";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",

    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },

  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  fileUpload: {
    marginBottom: 40,
  },

  heading: {
    fontSize: "1.2rem",
    lineHeight: "1.5rem",
    fontFamily: "Nunito, sans-serif",
    fontWeight: "bold",
    color: theme.palette.primary.dark,
  },

  label: {
    color: theme.palette.primary.main,
  },

  required: {
    "&::after": {
      content: '" *"',
    },
  },

  sectionHeader: {
    marginBottom: theme.spacing(2),
  },

  subheading: {
    fontSize: ".85rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  uploadSection: {
    marginTop: theme.spacing(3),
  },
}));

// Accessibility
const ids = {
  fileUpload: {
    button: "file-upload-button",
    label: "file-upload-label",
    error: "file-upload-error",
  },
  coverImage: {
    button: "cover-image-button",
    label: "cover-image-label",
    error: "cover-image-error",
  },
};

const isUniqueDisplay = (workType, workTypes) =>
  filter(propEq("display", workType.display), workTypes).length === 1;

const findNewFiles = (blobs, files) =>
  differenceWith(
    (blob, file) => blob.name === file.originalFilename,
    blobs,
    files,
  );

const getSuccessfulUploads = pipe(
  pluck("value"),
  flatten,
  filter(propEq("status", "fulfilled")),
  pluck("value"),
);

const mapInitialWorkFile = file => ({
  blob: file,
  progress: {
    percent: 0,
    timeRemaining: 0,
  },
  error: null,
  status: "loading",
  size: file.size,
  name: file.name,
});

const formatError = err => {
  if (err instanceof RequestError) {
    return {
      message:
        err.code === "ERR_CANCELED"
          ? "File upload canceled."
          : "Unable to complete upload.",
      status: err.code === "ERR_CANCELED" ? "aborted" : "failed",
    };
  } else {
    return {
      message: err.message,
      status: "failed",
    };
  }
};

const isMultiFileSubmission = workType =>
  includes(workType.submissionType, ["ALBUM"]);

const normalizeMimeType = blob => {
  // Browsers handle .wav file MIME type inconsistently;
  // force all wav files to be audio/wav
  if (includes(blob.type, ["audio/x-wav", "audio/wav"])) {
    return "audio/wav";
  }

  // "audio/x-m4a" and "audio/m4a" are deprecated
  // "audio/mpeg" is preferred
  if (includes(blob.type, ["audio/x-m4a", "audio/m4a"])) {
    return "audio/mpeg";
  }

  // certain versions of Firefox on Windows are showing "application/epub+zip" as "application/epub"
  // force them all to be "applicaton/epub+zip"
  if(includes(blob.type, ["application/epub+zip", "application/epub"])) {
    return "application/epub+zip"
  }

  return blob.type;
};

const Upload = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const submissionFiles = useSelector(getSubmissionFiles);
  const coverFile = useSelector(getCoverFile);
  const errors = useSelector(getStepErrors(steps.FILE_UPLOAD));
  const loading = useSelector(getStepLoadingLocalStorage(steps.FILE_UPLOAD));
  const workFiles = useSelector(getWorkFiles);
  const workType = useSelector(getWorkType);
  const workTypes = useSelector(getWorkTypes);
  const hideCoverImageUpload = useSelector(getProjectHideCoverImageUpload);

  const [showCoverImageUpload, setShowCoverImageUpload] = useState(true);
  useEffect(() => {
    setShowCoverImageUpload(
      !includes(workType.contentTypeName, ["IMAGE"]) && !hideCoverImageUpload,
    );
  }, [workType.contentTypeName, hideCoverImageUpload]);

  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  useEffect(() => {
    console.log("WorkTypes...");
    console.log(workTypes);

    const options = pipe(
      workTypes =>
        map(
          workType =>
            isUniqueDisplay(workType, workTypes)
              ? workType
              : assoc(
                  "display",
                  `${workType.display} (${workType.contentTypeDisplay})`,
                  workType,
                ),
          workTypes,
        ),
      sortBy(prop("display")),
      map(pick(["id", "display", "subTypeName"])),
    )(workTypes);

    console.log("Options...");
    console.log(options);

    setWorkTypeOptions(options);
  }, [workTypes]);

  //-- Handlers
  const setWorkFiles = pipe(changeWorkFiles, dispatch);
  const setSubmissionFiles = pipe(changeSubmissionFiles, dispatch);
  const addNewWorkFiles = pipe(updateWorkFiles, dispatch);
  const updateWorkFile = pipe(updateWorkFileUploadProgress, dispatch);
  const resetFiles = () => {
    setWorkFiles([]);
    setSubmissionFiles([]);
  };

  const setInitialCoverFile = pipe(setCoverFile, dispatch);
  const updateCoverFile = pipe(updateCoverFileUploadProgress, dispatch);

  const onCancel = () =>
    dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true });
  const onNext = () => {
    dispatch({ type: COMPLETE_STEP });
  };
  const onPrevious = () => {
    dispatch({ type: SET_ACTIVE_STEP, step: steps.BASIC_DETAILS });
  };

  const onChangeWorkTypeId = workTypeId => {
    resetFiles();
    dispatch({
      type: SET_WORK_TYPE_ID,
      workTypeId,
    });
  };
  const onWorkTypeFieldBlur = workTypeId => () =>
    dispatch({ type: WORK_TYPE_FIELD_BLURRED, workTypeId });

  const requestUploadUrl = useRequestUploadUrl();
  const uploadFile = useUploadFile();
  const [isUploading, setIsUploading] = useState(false);

  const handleChangeCoverFile = async fileList => {
    if (notBlank(fileList)) {
      setIsUploading(true);
      const fileBlob = fileList[0];
      setInitialCoverFile(mapInitialWorkFile(fileBlob));

      try {
        const fileData = mapUploadFileData(fileBlob, "COVER");
        const fileUploadResponse = await requestUploadUrl.mutateAsync(fileData);

        await uploadFile.mutateAsync({
          uploadUrl: fileUploadResponse.uploadUrl,
          fileBlob: fileBlob,
          progressEventHandler: e => {
            updateCoverFile(
              {
                percent: Math.floor((e.loaded / e.total) * 100),
                timeRemaining: Math.floor(e.estimated),
              },
              "loading",
            );
          },
        });

        // Finish Upload
        if (notBlank(fileUploadResponse.fileResource)) {
          updateCoverFile({ percent: 100, timeRemaining: 0 }, "successful");

          dispatch({
            type: UPDATE_SUBMISSION_FILES,
            fileResources: [fileUploadResponse.fileResource],
          });
        } else {
          throw new Error("An error occurred during upload. Please try again.");
        }
      } catch (err) {
        updateCoverFile(
          { percent: 0, timeRemaining: 0 },
          err.code === "ERR_CANCELED" ? "aborted" : "failed",
        );
      }

      setIsUploading(false);
    }
  };

  const handleChangeWorkFiles = async fileBlobs => {
    setIsUploading(true);
    const initialSubmissionFiles = isBlank(submissionFiles)
      ? []
      : submissionFiles;
    const newFiles = map(
      mapInitialWorkFile,
      findNewFiles(fileBlobs, initialSubmissionFiles),
    );

    if (notBlank(newFiles)) {
      if (includes(workType.submissionType, ["ALBUM"])) {
        addNewWorkFiles(newFiles);
      } else {
        setWorkFiles(newFiles);
      }

      const uploads = await Promise.allSettled(
        map(async group => {
          return await Promise.allSettled(
            map(async workFile => {
              try {
                const mimeType = normalizeMimeType(workFile.blob);

                // Manually validate MIME type
                if (
                  !includes(mimeType, workType.uploadFileAccepts.split(", "))
                ) {
                  throw Error(`${mimeType} is not an accepted file type.`);
                }

                const blob = new File([workFile.blob], workFile.blob.name, {
                  type: mimeType,
                });
                workFile.blob = blob;

                // max file size is 2 GB
                if (workFile.blob.size > 2000000000) {
                  throw new Error(
                    "File exceeds file size limit. Please upload a smaller file.",
                  );
                }
                // Get Upload URL
                const fileData = mapUploadFileData(workFile.blob);
                const fileUploadResponse = await requestUploadUrl.mutateAsync(
                  fileData,
                );

                // Upload File
                await uploadFile.mutateAsync({
                  uploadUrl: fileUploadResponse.uploadUrl,
                  fileBlob: workFile.blob,
                  progressEventHandler: e => {
                    updateWorkFile(
                      workFile,
                      {
                        percent: Math.floor((e.loaded / e.total) * 100),
                        timeRemaining: Math.floor(e.estimated),
                      },
                      "loading",
                    );
                  },
                });

                // Finish Upload
                if (notBlank(fileUploadResponse.fileResource)) {
                  updateWorkFile(
                    workFile,
                    { percent: 100, timeRemaining: 0 },
                    "successful",
                  );
                  return fileUploadResponse.fileResource;
                } else {
                  throw new Error(
                    "An error occurred during upload. Please try again.",
                  );
                }
              } catch (err) {
                const error = formatError(err);
                updateWorkFile(
                  workFile,
                  { percent: 0, timeRemaining: 0 },
                  error.status,
                  error.message,
                );
                return Promise.reject(error.message);
              }
            }, group),
          );
        }, splitEvery(5, newFiles)),
      );

      // Update submission files with new file resources
      const fileResources = getSuccessfulUploads(uploads);
      dispatch({
        type: UPDATE_SUBMISSION_FILES,
        fileResources: fileResources,
      });
    }

    setIsUploading(false);
  };

  const removeCoverFile = coverFile => {
    if (coverFile.status === "loading") {
      uploadFile.abort();
    }

    const updatedSubmissionFiles = reject(
      propEq("originalFilename", coverFile.name),
      submissionFiles,
    );

    setInitialCoverFile({});
    setSubmissionFiles(updatedSubmissionFiles);
  };

  const removeWorkFile = workFile => {
    if (workFile.status === "loading") {
      uploadFile.abort();
    }

    let updatedFiles;
    let updatedSubmissionFiles;

    // for multi-file submissions, remove one file at a time
    if (isMultiFileSubmission(workType)) {
      updatedFiles = reject(propEq("name", workFile.name), workFiles);
      updatedSubmissionFiles = reject(
        propEq("originalFilename", workFile.name),
        submissionFiles,
      );
    } else {
      // only reset files of type SUBMISSION
      updatedSubmissionFiles = reject(
        propEq("fileType", "SUBMISSION"),
        submissionFiles,
      );
      updatedFiles = [];
    }

    setWorkFiles(updatedFiles);
    setSubmissionFiles(updatedSubmissionFiles);
  };

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <div className={classes.container}>
        {Object.keys(workTypes).length > 1 && (
          <Fragment>
            <div className={classes.sectionHeader}>
              <Typography variant="h2" id="work-type-description">
                {t("upload_heading")}
              </Typography>
            </div>
            <form autoComplete="none" onSubmit={e => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <IntlAutocomplete
                    id="work-type"
                    disableClearable
                    autoSelect
                    options={workTypeOptions}
                    value={workType || ""}
                    getOptionLabel={wt =>
                      notBlank(wt)
                        ? t(`upload.work_types.${wt.id}`, wt.display)
                        : ""
                    }
                    getOptionSelected={(opt, val) =>
                      isBlank(val) || opt.id === val.id
                    }
                    onChange={(event, workType) =>
                      onChangeWorkTypeId(workType.id)
                    }
                    renderInput={params => {
                      params.inputProps["aria-autocomplete"] = "none";
                      params.inputProps["aria-labbeledby"] = "work-type";
                      params.inputProps["aria-describedby"] =
                        "work-type-description";
                      return (
                        <IntlTextField
                          {...params}
                          required
                          type="text"
                          onBlur={onWorkTypeFieldBlur(workType.id)}
                          error={hasError(errors, "workTypeId")}
                          helperText={t(firstError(errors, "workTypeId"))}
                          name={"workType-" + Math.random()}
                          variant="standard"
                          label={t("upload_work_type")}
                          fullWidth
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Fragment>
        )}
        {!isEmpty(workType) && (
          <div className={classes.uploadSection}>
            <form autoComplete="none">
              <Grid container spacing={2}>
                <Grid item xs={12} md={8} lg={4}>
                  <div className={classes.sectionHeader}>
                    <Typography
                      variant="h2"
                      id={ids.fileUpload.label}
                      className={classes.required}
                    >
                      {t(
                        "upload_directions",
                        `Upload your ${
                          workType.display === "Other"
                            ? "work"
                            : workType.display.toLowerCase()
                        }`,
                        {
                          type:
                            workType.display === "Other"
                              ? t("upload.work", "work")
                              : toLower(
                                  t(
                                    `upload.work_types.${workType.id}`,
                                    workType.display.toLowerCase(),
                                  ),
                                ),
                        },
                      )}
                    </Typography>
                    <Typography variant="subtitle1">
                      {t(
                        includes(workType.submissionType, ["ALBUM"])
                          ? "upload_directions_sub_plural"
                          : "upload_directions_sub",
                      )}
                    </Typography>
                  </div>
                  <FileUploadInput
                    buttonId={ids.fileUpload.button}
                    labelId={ids.fileUpload.label}
                    errorId={ids.fileUpload.error}
                    text={t(
                      includes(workType.submissionType, ["ALBUM"])
                        ? "file_upload_choose_files"
                        : "file_upload_choose_file",
                    )}
                    className={classes.fileUpload}
                    files={workFiles}
                    fileTypes={workType.uploadFileAcceptsDisplay}
                    accept={workType.uploadFileAccepts}
                    acceptMultiple={includes(workType.submissionType, [
                      "ALBUM",
                    ])}
                    onChange={handleChangeWorkFiles}
                    onRemove={removeWorkFile}
                    error={t(firstError(errors, "workFiles"))}
                  />
                </Grid>

                {showCoverImageUpload && (
                  <Grid item xs={12} md={8} lg={4}>
                    <div className={classes.sectionHeader}>
                      <Typography
                        id={ids.coverImage.label}
                        variant="h2"
                        className={clsx({
                          [classes.required]: workType.id === "ALBUM",
                        })}
                      >
                        {t("upload_cover")}
                      </Typography>
                      <Typography variant="subtitle1">
                        {t(
                          equals(workType.submissionType, "ALBUM")
                            ? "upload_cover_sub_album"
                            : "upload_cover_sub",
                          "Choose the cover image for the album.",
                        )}
                      </Typography>
                    </div>
                    <FileUploadInput
                      labelId={ids.coverImage.label}
                      buttonId={ids.coverImage.button}
                      errorId={ids.coverImage.error}
                      text={t("file_upload_choose_file")}
                      className={classes.fileUpload}
                      files={[coverFile]}
                      accept=".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"
                      acceptMultiple={false}
                      fileTypes={"JPG, PNG or GIF"}
                      onChange={handleChangeCoverFile}
                      onRemove={removeCoverFile}
                      error={t(firstError(errors, "coverFile"))}
                    />
                  </Grid>
                )}
              </Grid>
            </form>
          </div>
        )}
      </div>
      <StepPanelButtonBar
        isUploading={isUploading}
        onNext={onNext}
        onPrevious={onPrevious}
        onCancel={onCancel}
      />
    </Paper>
  );
};

export default Upload;
